import { isConfirmedInFlightStatus, ackPhaseFromStatus } from '@/lib/command-ack';
import type { DroneAckPayload } from '@/types/coordination';
import type { ManualFlyTarget } from './ManualMapControl';

/**
 * `FLY TO HERE`, dispatched in the one order the aircraft will accept — and
 * followed until the aircraft says what it did with it.
 *
 * The RC refuses `takeoff`, `start_mission` and `goto` for as long as a manual
 * control session holds the aircraft — `MissionController` answers
 * "a manual control session is active on this aircraft — release it before
 * commanding goto" — because a wayline and a set of sticks cannot both own an
 * airframe. A GO-TO dispatched from under a live session is therefore rejected
 * every single time, and the COP's own command bar already knows it: the GO-TO
 * key is disabled with that same sentence while manual authority is held.
 *
 * THE COP YIELDS. Authority goes back first, the release is awaited, and only a
 * release that was actually delivered dispatches the leg. That order is the
 * whole point of this module existing separately from the component that calls
 * it — it is a sequencing rule with an operational consequence, and it is
 * testable here without a map, a WebSocket or a browser.
 *
 * A release that did not land means the RC still holds the session (its own
 * dead-man is what will end it), so the GO-TO would come back rejected and the
 * operator would be told nothing useful. It is reported instead of sent.
 *
 * ## Why the outcome is watched, and not assumed
 *
 * Getting the order right is not enough, because the trade is one-way. The
 * release is irreversible — this console stops renewing the lock the moment it
 * asks — while the `goto` is a MOTOR command, and `MissionController` holds
 * every motor command until an operator ARM:
 *
 *     if (!_armed.value) holdMotorCommand(env) else executeMotorCommand(env)
 *
 * **A manual grant never arms.** `requestLocked` does not touch `_armed`, and
 * `abort`, `disarm` and `stop` all clear it. An aircraft flown up on the RC's
 * own sticks — the normal way a Mavic flies — is therefore unarmed in the air,
 * and so is one where the operator pressed ABORT before taking manual control.
 * For those the RC answers `held`: the leg is sitting on the controller waiting
 * for a physical press, the operator no longer has the sticks, and the aircraft
 * is doing nothing at all.
 *
 * `held` maps to the `acked` phase (`ackPhaseFromStatus`), which is correct for
 * a lifecycle readout and wrong as an outcome — read as success it means the
 * COP says the leg went while the operator has traded the sticks for a hover.
 * Before the release-first ordering existed, the same press produced an
 * immediate rejection while the operator still held the aircraft, which is
 * strictly better than that. So the ack is watched, and anything that is not the
 * AIRCRAFT confirming (`accepted` / `executing` / `done`) is reported as what it
 * is, with the sticks offered straight back.
 *
 * That verdict has a deadline; the command does not. What is said at the
 * deadline is therefore provisional, and `useManualFlyToOutcome` owns it
 * afterwards — it goes on following the same `command_id`, clears the strip if
 * the aircraft confirms late, and declines to write a verdict at all once the
 * operator has moved on (another aircraft, another leg, or the sticks retaken).
 */

/** Said when the aircraft could not be handed back, so the leg was not sent. */
export const MANUAL_FLY_TO_RELEASE_FAILED =
  'Fly here was not sent. Manual control could not be handed back, and the RC refuses it while it holds a manual session.';

/**
 * The RC is sitting on the leg waiting for an ARM, and manual control is gone.
 *
 * Names the trade that was actually made, in the order the operator experienced
 * it, and then the two ways out: press ARM (on the RC or the COP's ARM key,
 * which is the same `_armed` flag) to let the leg fly, or take the sticks back.
 */
export const MANUAL_FLY_TO_HELD =
  'Fly here is waiting for Arm on the RC. Manual control was handed back. Press Arm to let it fly, or take manual control again.';

/**
 * How long the leg's ack is waited for before the silence is itself the answer.
 *
 * The RC answers a motor command it is holding immediately — `tracker.held` is
 * published from the same branch that decides to hold it — and an accepted one
 * inside the SDK's own call. This window only has to cover a broker round trip
 * and the ingest hop, so it is short: past it the honest thing to tell an
 * operator who no longer has the sticks is that nothing has confirmed anything,
 * not to keep waiting silently. `useDroneCommand` goes on tracking the same
 * command id for its own 75 s lifecycle either way — this window ends the
 * FLY TO HERE flow's interest, not the command's.
 *
 * **It is a reporting deadline, not a verdict on the aircraft.** On an ARMED
 * aircraft the confirming ack is NOT immediate: `goto` builds a KMZ, writes it,
 * and calls `pushKMZFileToAircraft`, and only that upload's progress chain
 * produces `executing`/`accepted`. A slow wayline upload over a weak RF link
 * takes longer than this window, and the leg then starts anyway. So what is said
 * at the deadline claims only what was observed — see [manualFlyToUnconfirmed] —
 * and the acks go on being watched afterwards by `useManualFlyToOutcome`, which
 * clears the strip when the aircraft finally does confirm.
 */
export const MANUAL_FLY_TO_ACK_WINDOW_MS = 6_000;

/**
 * Nothing confirmed the leg: no ack inside the window, or a status that is not
 * the aircraft's own.
 *
 * This sentence used to end "so nothing is flying the leg", which is an
 * assertion the COP cannot make. Silence inside a 6 s window is evidence of
 * silence and nothing else: the RC may still be pushing the KMZ to the aircraft
 * (`FlightExecutor.goto` → `pushKMZFileToAircraft`), and the command bar — still
 * tracking this same `command_id` for its 75 s lifecycle — will render EXECUTING
 * the moment that upload lands. Two surfaces contradicting each other about one
 * aircraft is worse than either of them saying less, and the one that was wrong
 * was the one offering to take the sticks back.
 *
 * So the no-answer case reports the window it waited, names the upload as the
 * likely reason, and points at the surface that WILL know. A decisive but
 * non-confirming ack (`rejected`, `error`, `timed_out`) is a different fact —
 * the aircraft's own answer — and says only that, with that answer's own detail
 * when it carried one. `answered` is what separates the two: silence and a
 * refusal are not the same event, and only one of them may still be in progress.
 */
export function manualFlyToUnconfirmed(detail?: string | null, answered = false): string {
  const why = detail?.trim();
  const said = 'Fly here was not confirmed by the drone';
  const traded = 'manual control was already handed back.';
  if (why) return `${said} — ${traded} ${why}`;
  if (answered) return `${said} — ${traded}`;
  const seconds = Math.round(MANUAL_FLY_TO_ACK_WINDOW_MS / 1000);
  return `${said} within ${seconds} s — ${traded} The RC may still be uploading the leg: watch the command bar, and take manual control again if the aircraft does not start flying it.`;
}

/**
 * Said when a leg reported unconfirmed IS confirmed afterwards.
 *
 * The strip goes away on its own at that point, and a strip that vanishes with
 * no explanation is its own small ambiguity — the operator was told six seconds
 * ago that nothing had confirmed the leg. This says what changed, and repeats
 * the half of the trade that did not: the sticks are still handed back.
 */
export const MANUAL_FLY_TO_LATE_CONFIRMED =
  'Fly here confirmed by the drone. It is on its way. Manual control is still handed back.';

export type ManualFlyToOutcome =
  /** Released, dispatched, and the aircraft confirmed it. */
  | 'flying'
  /** Released and dispatched, but the RC is holding the leg for an ARM. */
  | 'held'
  /** Released and dispatched, and nothing confirmed it. */
  | 'unconfirmed'
  /** Nothing was sent: the aircraft could not be handed back. */
  | 'release-failed';

/** The one field of an ack this flow reads, plus what to say about it. */
export interface ManualFlyToAck {
  status: string | null;
  detail: string | null;
}

/**
 * Is this ack the aircraft's answer, or is the RC still talking about itself?
 *
 * `dispatched` means an SDK method was invoked and returned — nothing has been
 * confirmed — so it is NOT an answer and the watch keeps waiting for the one
 * that follows it. Every terminal word is an answer, including the failures.
 * An unrecognised word from a newer app build is treated as non-decisive: the
 * window closes on it, which reports "not confirmed" rather than inventing a
 * confirmation this COP cannot read.
 */
export function isDecisiveFlyToAck(status: string | null | undefined): boolean {
  if (!status) return false;
  const normalized = status.toLowerCase();
  if (normalized === 'held') return true;
  if (ackPhaseFromStatus(normalized) !== 'acked') return true;
  return isConfirmedInFlightStatus(normalized);
}

/** What the ack means for an operator who has already given up the sticks. */
export function flyToOutcomeFromAck(ack: ManualFlyToAck | null): Exclude<ManualFlyToOutcome, 'release-failed'> {
  if (!ack) return 'unconfirmed';
  const status = ack.status?.toLowerCase() ?? null;
  if (status === 'held') return 'held';
  if (isConfirmedInFlightStatus(status) || ackPhaseFromStatus(status) === 'done') return 'flying';
  return 'unconfirmed';
}

/**
 * The sentence for an outcome, or null when the leg is genuinely flying.
 *
 * `answered` says whether an ack was read at all, and only the `unconfirmed`
 * sentence cares: it is the difference between "the RC said no" and "we heard
 * nothing in six seconds", which are different facts about the aircraft.
 */
export function manualFlyToMessage(
  outcome: ManualFlyToOutcome,
  detail: string | null,
  answered = false,
): string | null {
  if (outcome === 'flying') return null;
  if (outcome === 'release-failed') return MANUAL_FLY_TO_RELEASE_FAILED;
  if (outcome === 'held') return MANUAL_FLY_TO_HELD;
  return manualFlyToUnconfirmed(detail, answered);
}

/**
 * Watch one dispatched command's acks until one of them is an answer.
 *
 * Resolves with the first decisive ack, or null when the window closes first.
 * Never rejects, and always unsubscribes — this runs after the confirm strip is
 * gone, so nothing is left to notice a leak.
 */
export function watchFlyToAck({
  commandId,
  subscribe,
  windowMs = MANUAL_FLY_TO_ACK_WINDOW_MS,
}: {
  commandId: string;
  subscribe: (cb: (ack: DroneAckPayload) => void) => () => void;
  windowMs?: number;
}): Promise<ManualFlyToAck | null> {
  return new Promise((resolve) => {
    let settled = false;
    let unsubscribe: (() => void) | null = null;
    const finish = (value: ManualFlyToAck | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      // The subscription may not exist yet if `subscribe` called back
      // synchronously; the guard below covers that ordering.
      unsubscribe?.();
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), windowMs);
    unsubscribe = subscribe((ack) => {
      if (ack.command_id !== commandId) return;
      if (!isDecisiveFlyToAck(ack.status)) return;
      finish({ status: ack.status ?? null, detail: ack.detail ?? null });
    });
    if (settled) unsubscribe();
  });
}

export interface ManualFlyToDispatch {
  target: ManualFlyTarget;
  /** `ManualControlController.release` — resolves false when it did not land. */
  release: () => Promise<boolean>;
  /**
   * `useDroneCommand().send` bound to the selected aircraft, resolving the
   * dispatched command's id — null when the POST itself failed, which that hook
   * has already reported.
   */
  send: (
    action: 'goto',
    params: { latitude: number; longitude: number; altitude_m: number; speed_ms: number },
  ) => Promise<string | null>;
  /** Follow that command id until the aircraft answers. See `watchFlyToAck`. */
  watchAck: (commandId: string) => Promise<ManualFlyToAck | null>;
  /**
   * The leg is on the wire and the sticks are gone: the confirm strip has done
   * its job. Called before the ack is waited for, so the operator is not left
   * looking at a "not sent yet" bar for a command that has been sent.
   */
  onDispatched?: () => void;
  /** How the operator is told the leg was not sent. */
  onReleaseFailed: (message: string) => void;
  /**
   * The leg was sent and the aircraft is NOT flying it *yet*. Carries the
   * sentence and the outcome, so the caller can offer the sticks back in one
   * press — and the `command_id`, because this verdict has a 6 s deadline while
   * the command has a 75 s life. The caller keeps watching that id (see
   * `useManualFlyToOutcome`) so a leg that starts late clears what was said
   * about it here, instead of leaving a strip contradicting the command bar.
   * Null when the POST never produced an id, which is nothing to follow.
   */
  onNotFlying: (
    message: string,
    outcome: 'held' | 'unconfirmed',
    commandId: string | null,
  ) => void;
}

export async function dispatchManualFlyTo({
  target,
  release,
  send,
  watchAck,
  onDispatched,
  onReleaseFailed,
  onNotFlying,
}: ManualFlyToDispatch): Promise<ManualFlyToOutcome> {
  const released = await release();
  if (!released) {
    onReleaseFailed(MANUAL_FLY_TO_RELEASE_FAILED);
    return 'release-failed';
  }
  const commandId = await send('goto', {
    latitude: target.lat,
    longitude: target.lng,
    altitude_m: target.altitudeM,
    speed_ms: target.speedMs,
  });
  onDispatched?.();
  // No command id means the POST never produced one — `useDroneCommand` has
  // already said so, and there is nothing to correlate an ack against. It is
  // still not flying, and the operator has still lost the sticks.
  const ack = commandId ? await watchAck(commandId) : null;
  const outcome = flyToOutcomeFromAck(ack);
  if (outcome !== 'flying') {
    const message = manualFlyToMessage(outcome, ack?.detail ?? null, ack != null);
    if (message) onNotFlying(message, outcome, commandId);
  }
  return outcome;
}
